import { useState } from "react";
import { palette } from "./pageStyles";
import RuleBuilder from "./RuleBuilder";
import { deleteItem, deleteRule, newId, requestEdit, saveRule } from "../backtest/labStore";

const st = {
  row: { display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" },
  small: { fontSize: "0.75rem", color: palette.mutedText },
  h: { margin: 0, fontSize: "0.95rem", fontWeight: 600 },
  sub: { margin: "6px 0 0", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: palette.mutedText },
  field: { padding: "7px 9px", borderRadius: "4px", border: `1px solid ${palette.border}`, background: "#050608", color: palette.text, fontSize: "0.85rem", outline: "none" },
  btn: { padding: "9px 18px", borderRadius: "4px", border: "none", background: palette.accent, color: palette.accentText, fontWeight: 700, fontSize: "0.9rem", cursor: "pointer" },
  ghost: { padding: "7px 12px", borderRadius: "4px", border: `1px solid ${palette.border}`, background: "transparent", color: palette.text, fontSize: "0.8rem", cursor: "pointer" },
  mini: { padding: "3px 8px", borderRadius: "4px", border: `1px solid ${palette.border}`, background: "transparent", color: palette.text, fontSize: "0.72rem", cursor: "pointer" },
  lbl: { display: "flex", flexDirection: "column", gap: "3px", fontSize: "0.72rem", color: palette.mutedText },
  tag: { fontSize: "0.65rem", padding: "1px 6px", borderRadius: 3, border: `1px solid ${palette.border}`, color: palette.mutedText },
};

function ParamBoxes({ entryId, specs, params, setParam }) {
  const values = params[entryId] || {};
  const visibleSpecs = specs.filter((param) => !param.showWhen || param.showWhen(values));
  if (!visibleSpecs.length) return <div style={st.small}>This one has no settings. Add some in the code with param("name", default, min, max).</div>;
  return (
    <div style={{ ...st.row, marginTop: 6 }}>
      {visibleSpecs.map((p) => (
        <label key={p.key} style={st.lbl}>
          {p.label}
          {p.type === "select" ? (
            <select
              style={{ ...st.field, minWidth: 190, maxWidth: "100%" }}
              value={params[entryId]?.[p.key] ?? p.value}
              onChange={(e) => setParam(entryId, p.key, e.target.value)}
            >
              {(p.options || []).map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          ) : (
            <input
              style={{ ...st.field, width: 80 }}
              type="number"
              min={p.min}
              max={p.max}
              step={p.step || "any"}
              value={params[entryId]?.[p.key] ?? p.value}
              onChange={(e) => setParam(entryId, p.key, e.target.value)}
            />
          )}
        </label>
      ))}
    </div>
  );
}

function ConfirmDelete({ name, onYes }) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <button type="button" style={st.mini} onClick={() => setAsking(true)} aria-label={`Delete ${name}`}>
        Delete
      </button>
    );
  }
  return (
    <span style={{ ...st.row, gap: 4 }}>
      <span style={{ ...st.small, color: palette.danger }}>Delete?</span>
      <button type="button" style={{ ...st.mini, borderColor: palette.danger, color: palette.danger }} onClick={onYes}>Yes</button>
      <button type="button" style={st.mini} onClick={() => setAsking(false)}>No</button>
    </span>
  );
}

function Entry({ entry, checked, onCheck, open, onToggle, children, actions, tag }) {
  return (
    <div style={{ borderBottom: `1px solid ${palette.border}`, paddingBottom: 6 }}>
      <div style={{ ...st.row, justifyContent: "space-between" }}>
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: "0.88rem", minWidth: 0 }}>
          {onCheck ? <input type="checkbox" checked={checked} onChange={(e) => onCheck(e.target.checked)} /> : null}
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{entry.name}</span>
          {tag && <span style={st.tag}>{tag}</span>}
        </label>
        <span style={{ ...st.row, gap: 4 }}>
          {onToggle && (
            <button type="button" style={st.mini} onClick={onToggle} aria-expanded={!!open}>
              {open ? "Hide settings" : "Settings"}
            </button>
          )}
          {actions}
        </span>
      </div>
      {entry.blurb && <div style={st.small}>{entry.blurb}</div>}
      {open && children}
    </div>
  );
}

/** "3. Strategies": everything the backtest can run, with settings but without code. */
export default function StrategyLibrary({ catalog, items, selected, setSelected, params, setParam, onRun, running, error, onOpenEditor, isAdmin = false, adminScanControls = null }) {
  const [openParams, setOpenParams] = useState({});
  const [ruleEditor, setRuleEditor] = useState(null); // null | {rule?} (new when rule is undefined)
  const indicators = items.filter((it) => it.kind === "indicator");
  const toggle = (id) => setOpenParams((prev) => ({ ...prev, [id]: !prev[id] }));
  const check = (id) => (on) => setSelected((prev) => ({ ...prev, [id]: on }));

  const builtin = catalog.filter((e) => e.kind === "builtin");
  const coded = catalog.filter((e) => e.kind === "code");
  const ruled = catalog.filter((e) => e.kind === "rule");

  const editCode = (id) => {
    onOpenEditor();
    requestEdit(id);
  };
  const newCode = (kind) => {
    onOpenEditor();
    requestEdit(`new:${kind}`);
  };

  return (
    <>
      <h3 style={st.h}>3. Strategies</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={st.sub}>Built-in</div>
        {builtin.map((e) => (
          <Entry key={e.id} entry={e} tag={e.adminOnly ? "Admin" : undefined} checked={!!selected[e.id]} onCheck={check(e.id)} open={openParams[e.id]} onToggle={() => toggle(e.id)}>
            <ParamBoxes entryId={e.id} specs={e.params} params={params} setParam={setParam} />
          </Entry>
        ))}

        <div style={st.sub}>My strategies (Python)</div>
        {!coded.length && <div style={st.small}>None yet. Write one in the code editor below and press "Add to backtest".</div>}
        {coded.map((e) => (
          <Entry
            key={e.id}
            entry={e}
            tag="Python"
            checked={!!selected[e.id]}
            onCheck={check(e.id)}
            open={openParams[e.id]}
            onToggle={() => toggle(e.id)}
            actions={
              <>
                <button type="button" style={st.mini} onClick={() => editCode(e.id)}>Edit code</button>
                <ConfirmDelete name={e.name} onYes={() => deleteItem(e.id)} />
              </>
            }
          >
            <ParamBoxes entryId={e.id} specs={e.params} params={params} setParam={setParam} />
          </Entry>
        ))}

        <div style={st.sub}>Rule strategies (no code)</div>
        {!ruleEditor && <div style={st.small}>Create a rule strategy to browse chart indicators by category, including moving averages, oscillators, volatility, volume and candlestick signals.</div>}
        {!ruleEditor && isAdmin && <div style={st.small}>For regime rules, choose Kaufman trend score, set its period and smoothing, then compare it with a number to define the Trend or Range cutoff.</div>}
        {ruled.map((e) =>
          ruleEditor?.rule?.id === e.id ? (
            <RuleBuilder
              key={e.id}
              initial={e.rule}
              indicators={indicators}
              isAdmin={isAdmin}
              onCancel={() => setRuleEditor(null)}
              onSave={(rule) => {
                saveRule({ ...rule, id: e.id });
                setRuleEditor(null);
              }}
            />
          ) : (
            <Entry
              key={e.id}
              entry={e}
              tag="Rules"
              checked={!!selected[e.id]}
              onCheck={check(e.id)}
              actions={
                <>
                  <button type="button" style={st.mini} onClick={() => setRuleEditor({ rule: e.rule })}>Edit</button>
                  <ConfirmDelete name={e.name} onYes={() => deleteRule(e.id)} />
                </>
              }
            />
          )
        )}
        {ruleEditor && !ruleEditor.rule && (
          <RuleBuilder
            indicators={indicators}
            isAdmin={isAdmin}
            onCancel={() => setRuleEditor(null)}
            onSave={(rule) => {
              const saved = saveRule({ ...rule, id: newId("rule") });
              setSelected((prev) => ({ ...prev, [saved.id]: true }));
              setRuleEditor(null);
            }}
          />
        )}

        <div style={st.sub}>My indicators (Python)</div>
        {!indicators.length && <div style={st.small}>None yet. Code one that calls output(values); it then shows up in the rule builder's lists.</div>}
        {indicators.map((it) => (
          <Entry
            key={it.id}
            entry={{ name: it.name, blurb: it.outputs.length > 1 ? `Lines: ${it.outputs.join(", ")}` : "" }}
            tag="Indicator"
            open={openParams[it.id]}
            onToggle={() => toggle(it.id)}
            actions={
              <>
                <button type="button" style={st.mini} onClick={() => editCode(it.id)}>Edit code</button>
                <ConfirmDelete name={it.name} onYes={() => deleteItem(it.id)} />
              </>
            }
          >
            <ParamBoxes entryId={it.id} specs={it.params} params={params} setParam={setParam} />
          </Entry>
        ))}
      </div>

      <div style={st.row}>
        <button type="button" style={st.ghost} onClick={() => setRuleEditor({})} disabled={!!ruleEditor}>+ Rule strategy (no code)</button>
        <button type="button" style={st.ghost} onClick={() => newCode("strategy")}>+ Code a strategy</button>
        <button type="button" style={st.ghost} onClick={() => newCode("indicator")}>+ Code an indicator</button>
      </div>
      <div style={st.row}>
        <button type="button" style={st.btn} onClick={onRun} disabled={!!running}>{running ? "Running..." : "Run backtest"}</button>
        <button type="button" style={st.ghost} onClick={() => setSelected(Object.fromEntries(catalog.map((e) => [e.id, true])))}>Select all</button>
        <button type="button" style={st.ghost} onClick={() => setSelected({})}>Clear</button>
        {running && <span style={st.small}>{running}</span>}
        {error && <span style={{ color: palette.danger, fontSize: "0.82rem" }}>{error}</span>}
      </div>
      {isAdmin && adminScanControls}
    </>
  );
}
